"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { api } from "~/trpc/react";
import type { DailyAvailability } from "./therapist-card/types";

export type DailyAvailabilityDay = { date: string; slotCount: number };

type DailyAvailabilityContextValue = {
  daysBySlug: Record<string, DailyAvailabilityDay[]>;
  unavailableSlugs: Set<string>;
};

// Mirrors the resolver's Zod cap so chunked queries always validate.
const BATCH_CHUNK_SIZE = 50;

const DailyAvailabilityContext =
  createContext<DailyAvailabilityContextValue | null>(null);

/**
 * A failed query never produces per-day data, not even zero-count entries.
 * Returning `{state: "ready", days: []}` here would make the card render its
 * "no slots" copy with an empty day grid: no tiles at all, unlike the
 * honest all-eight-days-greyed week the server returns for a real quiet
 * week. That reads as a broken grid, not an honest empty state, so a failed
 * or still-pending slug stays unresolved instead.
 */
export function useDailyAvailability(slug: string): DailyAvailability {
  const ctx = useContext(DailyAvailabilityContext);
  if (!ctx) {
    return { state: "loading" };
  }

  const days = ctx.daysBySlug[slug];
  if (days) {
    return { state: "ready", days };
  }

  if (ctx.unavailableSlugs.has(slug)) {
    return { state: "unavailable" };
  }

  return { state: "loading" };
}

interface DailyAvailabilityProviderProps {
  therapistSlugs: string[];
  sessionType: "INDIVIDUAL" | "COUPLES";
  days?: number;
  children: ReactNode;
}

function getViewerTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function DailyAvailabilityProvider({
  therapistSlugs,
  sessionType,
  days = 8,
  children,
}: DailyAvailabilityProviderProps) {
  const viewerTimezone = getViewerTimezone();
  // Stabilise the slug list so identical content does not retrigger the query.
  const stableSlugs = useMemo(
    () => [...therapistSlugs].sort(),
    [therapistSlugs],
  );

  // Chunk slug list to stay under the resolver's per-call cap. Lists below the
  // cap fire a single query; larger lists fan out and are merged in the value.
  const chunks = useMemo(() => {
    const out: string[][] = [];
    for (let i = 0; i < stableSlugs.length; i += BATCH_CHUNK_SIZE) {
      out.push(stableSlugs.slice(i, i + BATCH_CHUNK_SIZE));
    }
    return out;
  }, [stableSlugs]);

  const queries = api.useQueries((t) =>
    chunks.map((chunk) =>
      t.calendar.getDailyAvailabilityBatch(
        { therapistSlugs: chunk, days, sessionType, viewerTimezone },
        { staleTime: 1000 * 60 * 5 },
      ),
    ),
  );

  const value = useMemo<DailyAvailabilityContextValue>(() => {
    const daysBySlug: Record<string, DailyAvailabilityDay[]> = {};
    const unavailableSlugs = new Set<string>();

    queries.forEach((q, index) => {
      if (q.data) {
        for (const [slug, dayEntries] of Object.entries(q.data)) {
          daysBySlug[slug] = dayEntries;
        }
      } else if (q.isError) {
        for (const slug of chunks[index] ?? []) {
          unavailableSlugs.add(slug);
        }
      }
    });

    return {
      daysBySlug,
      unavailableSlugs,
    };
  }, [chunks, queries]);

  return (
    <DailyAvailabilityContext.Provider value={value}>
      {children}
    </DailyAvailabilityContext.Provider>
  );
}
